import { Suspense, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { useUserStore } from "@/lib/fetch-users"
import { getFormattedDisplayName, getMessageContent, getMessageRole } from "@/modules/chats/lib/format-chat-content"
import { parseAiFeatures } from "@/modules/chats/lib/map-chat-message"
import { SofiaChatMessage } from "@/modules/chats/types/chat-message"
import ChatTranscriptContent from "@/modules/patients/detail/progress-note/components/chat-transcript-content"
import { useChatTranscriptByAppointmentId } from "@/modules/patients/detail/progress-note/hooks/useChatTranscriptQuery"
import { parseChatTranscript } from "@/modules/patients/libs/parse-chat-transcript"
import { useDrawerStore } from "@/stores/useDrawerStore"

type ChatTranScriptProps = {
  patientId: string
  appointmentId: string
}

const ChatTranscriptDrawer = (props: ChatTranScriptProps) => {
  const { t } = useTranslation()
  const { appointmentId, patientId } = props
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const users = useUserStore((state) => state.users)
  const language = useCurrentLanguage()

  const chatTranscriptDataByAppointmentId = useChatTranscriptByAppointmentId(appointmentId)
  const { historyMessages } = useMemo(() => {
    if (!chatTranscriptDataByAppointmentId.appointmentById?.chatTranscript) {
      return { historyMessages: [], acsIdentities: [] }
    }

    const messages = parseChatTranscript(chatTranscriptDataByAppointmentId.appointmentById.chatTranscript).filter(
      (e) => e.acsSenderIdentity,
    )
    const uniqueAcsIdentities = [...new Set(messages.map((message) => message.acsSenderIdentity))].filter(
      (acsSenderIdentity): acsSenderIdentity is string => acsSenderIdentity !== undefined,
    )

    return { historyMessages: messages, acsIdentities: uniqueAcsIdentities }
  }, [chatTranscriptDataByAppointmentId])

  const [chatTranscriptMessagesContent, setChatTranscriptMessagesContent] = useState<SofiaChatMessage[]>([])

  useEffect(() => {
    if (!users) {
      setChatTranscriptMessagesContent([])
      return
    }

    const usersByAcsId = new Map(Array.from(users.values()).map((u) => [u.acsUserId, u]))

    const messagesWithRole = historyMessages.map((message) => {
      const sofiaUserInformation = usersByAcsId.get(message.acsSenderIdentity)
      const senderDisplayName = getFormattedDisplayName(sofiaUserInformation)

      const newMessage: SofiaChatMessage = {
        ...message,
        senderDisplayName,
        role: getMessageRole(message.type, !!sofiaUserInformation),
        content: getMessageContent(message.type, message.content, message.sentAt, senderDisplayName, language),
        metadata: {
          originalContent: message.metadata?.originalContent,
          aiFeatures: parseAiFeatures(message.metadata?.aiFeatures),
        },
      }

      return newMessage
    })

    setChatTranscriptMessagesContent(messagesWithRole.reverse())
  }, [historyMessages, setChatTranscriptMessagesContent, users, language])

  const handleDrawerClose = () => {
    closeDrawer()
  }
  return (
    <Drawer open onOpenChange={handleDrawerClose}>
      <DrawerContent data-testid="chat-transcript-drawer-content">
        <DrawerClose className="top-2 right-2" />
        <DrawerHeader className="gap-4 pr-4">
          <DrawerTitle
            className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium"
            data-testid="chat-transcript-drawer-title">
            {t("chat.titleTranscript")}
          </DrawerTitle>
        </DrawerHeader>
        <DrawerBody className="p-8">
          <Suspense fallback={<LoadingSpinner isFullPage={true} size={24} />}>
            <ChatTranscriptContent
              chatTranscriptMessagesContent={chatTranscriptMessagesContent}
              patientId={patientId}
              appointmentId={appointmentId}></ChatTranscriptContent>
          </Suspense>
        </DrawerBody>
        <DrawerFooter className="justify-end gap-2 overflow-hidden">
          <Button variant="secondary" onClick={closeDrawer} data-testid="close-button">
            {t("common.button.close")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default ChatTranscriptDrawer
